// SPDX-FileCopyrightText: 2025 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { GetModelTitleTranslationKey, ProjectVersion } from 'model/ProjectMetaMessageExtensions';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import React from 'react';
import { EvaluateQCProgramCompleteMessage } from '@tls/sw91-communication/types/com.api_predictor';
import AlgorithmResultsTable from './AlgorithmResultsTable';
import EvaluationConfusionMatrix from './EvaluationConfusionMatrix';
import { Colors, OutputValue, ProgressBar } from '@tls/treact-ui';
interface Props {
  version: ProjectVersion;
  evaluationResult?: EvaluateQCProgramCompleteMessage;
  progress: number;
}
const AlgorithmResult = ({ version, evaluationResult, progress }: Props) => {
  const { t } = useTranslation();

  if (!evaluationResult) {
    return (
      <Root>
        <span className='font-size_l'>{t(GetModelTitleTranslationKey(version), { version: version ?? '' })}</span>

        <ResultBox>
          <ProgressBarWrapper>
            <ProgressBar percentage={progress * 100} data-testid={`evaluation-algorithm-${version}-progress`} />
          </ProgressBarWrapper>
        </ResultBox>
      </Root>
    );
  }

  return (
    <Root>
      <span className='font-size_l'>{t(GetModelTitleTranslationKey(version), { version: version ?? '' })}</span>
      <MatrixContainer>
        <EvaluationConfusionMatrix version={version} confusionMatrix={evaluationResult.confusionMatrix} />
        <ValueWrapper>
          <OutputValue
            asAtom
            unit='%'
            label={t('project.evaluation.qc.confusionMatrix.values.accuracy')}
            value={formatAsPercent(evaluationResult.accuracy ?? 0)}
            data-testid={`evaluation-algorithm-${version}-accuracy-value`}
          />
          <OutputValue
            asAtom
            unit='%'
            label={t('project.evaluation.qc.confusionMatrix.values.escapeRate')}
            value={formatAsPercent(evaluationResult.escapeRate ?? 0)}
            data-testid={`evaluation-algorithm-${version}-escape-rate-value`}
          />
          <OutputValue
            asAtom
            unit='%'
            label={t('project.evaluation.qc.confusionMatrix.values.overkillRate')}
            value={formatAsPercent(evaluationResult.overkillRate ?? 0)}
            data-testid={`evaluation-algorithm-${version}-overkill-rate-value`}
          />
          <OutputValue
            asAtom
            unit='%'
            label={t('project.evaluation.qc.confusionMatrix.values.precision')}
            value={formatAsPercent(evaluationResult.precision ?? 0)}
            data-testid={`evaluation-algorithm-${version}-precision-value`}
          />
          <OutputValue
            asAtom
            unit='%'
            label={t('project.evaluation.qc.confusionMatrix.values.f1Score')}
            value={formatAsPercent(evaluationResult.f1Score ?? 0)}
            data-testid={`evaluation-algorithm-${version}-f1-score-value`}
          />
        </ValueWrapper>
      </MatrixContainer>

      <ResultBox>
        <AlgorithmResultsTable algorithmResults={evaluationResult?.algorithmResults} />
      </ResultBox>
    </Root>
  );
};
export function formatAsPercent(value: number): number {
  // Multipliziere mit 100 und runde auf 2 Nachkommastellen
  const percentValue = (value * 100).toFixed(2);

  // Füge das Prozentzeichen hinzu und gib den formatierten String zurück
  return parseFloat(percentValue);
}

const Root = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  justify-content: center;
`;
const ResultBox = styled.div`
  height: 1px;
  flex-grow: 1;
  border: 1px solid ${Colors.trgrey5.hex};
  overflow: hidden;
  background-color: ${Colors.trwhite.hex};
`;
const MatrixContainer = styled.div`
  height: 20rem;
  border: 1px solid ${Colors.trgrey5.hex};
  display: flex;
  flex-direction: column;
  background-color: ${Colors.trwhite.hex};
`;
// Uncomment when export logic is added
// const ButtonContainer = styled.div`
//   height: 2.5rem;
//   width: 100%;
//   display: flex;
//   align-items: center;
//   justify-content: center;
// `;
const ValueWrapper = styled.div`
  height: 5rem;
  border-top: 1px solid ${Colors.trgrey5.hex};
  display: flex;
  gap: 1rem;
  padding: 0 2rem;
  align-items: center;
  justify-content: space-between;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;
export default AlgorithmResult;
